// Small reusable UI helpers
import React from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

export function PageHeader({ overline, title, actions, description }) {
  return (
    <div className="flex items-start justify-between gap-6 mb-6" data-testid="page-header">
      <div>
        {overline && (
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">{overline}</div>
        )}
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-950">{title}</h1>
        {description && <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="shrink-0 flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function KpiCard({ label, value, sub, testid, accent = "slate" }) {
  const accents = {
    slate: "text-slate-950",
    red: "text-red-600",
    green: "text-emerald-600",
    amber: "text-amber-600",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-3">{label}</div>
      <div className={`font-heading font-bold text-3xl tracking-tighter ${accents[accent] || accents.slate}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", t: "Aktif" },
    inactive: { c: "bg-slate-100 text-slate-600 border-slate-200", t: "Pasif" },
    paused: { c: "bg-slate-100 text-slate-600 border-slate-200", t: "Pasif" },
    deleted: { c: "bg-red-50 text-red-700 border-red-200", t: "Silindi" },
  };
  const m = map[status] || { c: "bg-slate-100 text-slate-600 border-slate-200", t: status };
  return (
    <span className={`inline-flex text-[10.5px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border ${m.c}`}>
      {m.t}
    </span>
  );
}

export function EmptyState({ title, subtitle, testid }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-md p-10 text-center bg-white" data-testid={testid || "empty-state"}>
      <div className="font-heading text-base font-semibold text-slate-800">{title}</div>
      {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
    </div>
  );
}

/**
 * DataTable with optional client-side column sorting.
 * Give a column `sortValue: (row) => string | number` to make its header clickable.
 * `defaultSort` = { key: <column.sortKey>, dir: "asc" | "desc" }.
 */
export function DataTable({ columns, rows, testid, empty = "Kayıt bulunamadı", defaultSort = null }) {
  const [sort, setSort] = React.useState(defaultSort);

  const sorted = React.useMemo(() => {
    if (!sort || !rows) return rows;
    const idx = columns.findIndex((c) => (c.sortKey || "") === sort.key);
    const col = columns[idx];
    if (!col || !col.sortValue) return rows;
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = col.sortValue(a);
      const vb = col.sortValue(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va ?? "").localeCompare(String(vb ?? ""), "tr", { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [rows, sort, columns]);

  function toggle(key) {
    setSort((s) => (s && s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }

  if (!rows || rows.length === 0) {
    return <EmptyState title={empty} testid={testid ? `${testid}-empty` : undefined} />;
  }
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md bg-white" data-testid={testid}>
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {columns.map((c, i) => {
              const key = c.sortKey || "";
              const sortable = !!c.sortValue && !!key;
              const activeDir = sort && sort.key === key ? sort.dir : null;
              return (
                <th key={i} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-900 transition-colors"
                      data-testid={`sort-${key}`}
                    >
                      {c.label}
                      {activeDir === "asc" && <ArrowUp className="w-3 h-3 text-red-600" />}
                      {activeDir === "desc" && <ArrowDown className="w-3 h-3 text-red-600" />}
                      {!activeDir && <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                    </button>
                  ) : c.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
              {columns.map((c, j) => (
                <td key={j} className="px-4 py-3 align-middle text-slate-800">
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
